// src/components/UpcomingElectionsComponents/CalendarModal.jsx
//
// Shared "Add to Calendar" modal for FEC, Google Civic, Democracy Works, and
// the Upcoming Elections table. Most callers pass a simple
// `{ election_name, election_date, election_notes }` payload — that case still
// works exactly as before. Democracy Works additionally passes a
// `dateOptions` array so the user can pick which deadline to add (Election Day,
// Register by, Mail ballot, Early voting, etc.) — same UX as the mobile app.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { IoIosClose } from "react-icons/io";
import {
  addElectionToGoogleCalendar,
  getOutlookCalendarLink,
  downloadIcsFile,
} from "../../utils/calendarUtils";
import { FaRegCalendarPlus } from "react-icons/fa6";
import { FaChevronDown, FaChevronUp, FaCheck } from "react-icons/fa6";

const brandDark = "#047857";
const brandGradient = "linear-gradient(135deg, #047857 0%, #059669 100%)";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 50,
  backgroundColor: "rgba(15, 23, 42, 0.4)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const cardStyle = {
  position: "relative",
  width: "100%",
  maxWidth: 448,
  borderRadius: 16,
  backgroundColor: "#fff",
  boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
  padding: 24,
};

const closeButtonStyle = {
  position: "absolute",
  right: 8,
  top: 8,
  width: 36,
  height: 36,
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  border: "none",
  background: "transparent",
  color: "#64748b",
  cursor: "pointer",
};

const primaryBtnStyle = {
  borderRadius: 12,
  padding: "10px 16px",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#fff",
  border: "none",
  cursor: "pointer",
  background: brandGradient,
  minHeight: 44,
};

const secondaryBtnStyle = {
  borderRadius: 12,
  padding: "10px 16px",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#1e293b",
  border: "1px solid #cbd5e1",
  backgroundColor: "#fff",
  cursor: "pointer",
  minHeight: 44,
};

const disabledStyle = {
  opacity: 0.5,
  cursor: "not-allowed",
};

const dropdownTriggerStyle = {
  marginTop: 10,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 10,
  backgroundColor: "rgba(4, 120, 87, 0.06)",
  border: "1px solid rgba(4, 120, 87, 0.2)",
  cursor: "pointer",
  color: "#0f172a",
  textAlign: "left",
  fontFamily: "inherit",
};

const dropdownMenuStyle = {
  marginTop: 8,
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  backgroundColor: "#f8fafc",
  maxHeight: 220,
  overflowY: "auto",
};

const dropdownRowBase = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "10px 12px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0",
  fontFamily: "inherit",
};

const formatDate = (iso) => {
  if (!iso) return "Date TBD";
  try {
    const normalized = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + "T00:00:00" : iso;
    return new Date(normalized).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function CalendarModal({ open, onClose, election }) {
  const [calError, setCalError] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Build the date options the user can pick from. If the caller supplied
  // `dateOptions` (Democracy Works does), use those. Otherwise synthesize a
  // single "Election Day" option from the legacy fields so existing callers
  // (FEC, Google Civic, Upcoming Elections table) keep working unchanged.
  const dateOptions = useMemo(() => {
    if (!election) return [];
    if (Array.isArray(election.dateOptions) && election.dateOptions.length) {
      return election.dateOptions;
    }
    return [
      {
        id: "election_day",
        label: "Election Day",
        date: election.election_date || "",
        calendarTitle: election.election_name || "Election",
        notesPrefix: null,
      },
    ];
  }, [election]);

  useEffect(() => {
    setSelectedOptionId(dateOptions[0]?.id || null);
    setDropdownOpen(false);
    setCalError(null);
  }, [election, dateOptions]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (dropdownOpen) {
        setDropdownOpen(false);
        return;
      }
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, dropdownOpen]);

  const stop = useCallback((e) => e.stopPropagation(), []);

  const selectedOption =
    dateOptions.find((o) => o.id === selectedOptionId) || dateOptions[0] || null;
  const showDropdown = dateOptions.length > 1;

  // Build the payload that actually gets handed to the calendar utilities,
  // with the selected option's title/date/notes-prefix layered on top of the
  // election's base fields.
  const buildEffectiveElection = () => {
    if (!election) return null;
    if (!selectedOption) return election;
    const baseNotes = election.election_notes || "";
    const prefixedNotes = selectedOption.notesPrefix
      ? `${selectedOption.notesPrefix}\n\n${baseNotes}`
      : baseNotes;
    return {
      ...election,
      election_name: selectedOption.calendarTitle || election.election_name,
      election_date: selectedOption.date || election.election_date,
      election_notes: prefixedNotes,
    };
  };

  const handleAddToGoogle = async () => {
    setCalError(null);
    try {
      const link = await addElectionToGoogleCalendar(buildEffectiveElection());
      if (link) window.open(link, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      setCalError(err.message || "Could not add to Google Calendar.");
    }
  };

  const handleAddToOutlook = () => {
    setCalError(null);
    try {
      const link = getOutlookCalendarLink(buildEffectiveElection());
      if (link) window.open(link, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      setCalError("Could not open Outlook Calendar.");
    }
  };

  const handleAddToIcs = () => {
    setCalError(null);
    try {
      downloadIcsFile(buildEffectiveElection());
    } catch (err) {
      console.error(err);
      setCalError("Could not generate iCal file.");
    }
  };

  if (!open) return null;

  const name = election?.election_name ?? "Election";
  const disabled = !election || !selectedOption?.date;

  const handleOverlayClick = () => {
    if (dropdownOpen) {
      setDropdownOpen(false);
      return;
    }
    onClose?.();
  };

  return (
    <div
      style={overlayStyle}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Add election to calendar"
    >
      <div style={cardStyle} onClick={stop}>
        <button
          onClick={onClose}
          style={closeButtonStyle}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f1f5f9")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          aria-label="Close"
          title="Close"
        >
          <IoIosClose size={22} />
        </button>

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "inline-flex",
              width: 36,
              height: 36,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 12,
              color: "#fff",
              background: brandDark,
            }}
            aria-hidden="true"
          >
            <FaRegCalendarPlus />
          </div>
          <h2 style={{ marginTop: 12, fontSize: "1.125rem", fontWeight: 700, color: "#0f172a" }}>
            Add to calendar
          </h2>
          <p style={{ marginTop: 4, fontSize: "0.875rem", color: "#475569" }}>
            <span style={{ fontWeight: 600 }}>{name}</span>
          </p>

          {showDropdown ? (
            <>
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                style={dropdownTriggerStyle}
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
              >
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span style={{ fontSize: "0.75rem", fontWeight: 600, color: brandDark }}>
                    {selectedOption?.label}
                  </span>
                  <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#0f172a" }}>
                    {formatDate(selectedOption?.date)}
                  </span>
                </div>
                {dropdownOpen ? (
                  <FaChevronUp size={14} color={brandDark} />
                ) : (
                  <FaChevronDown size={14} color={brandDark} />
                )}
              </button>

              {dropdownOpen && (
                <div style={dropdownMenuStyle} role="listbox">
                  {dateOptions.map((option, idx) => {
                    const isSelected = option.id === selectedOptionId;
                    const isLast = idx === dateOptions.length - 1;
                    return (
                      <button
                        type="button"
                        key={option.id}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => {
                          setSelectedOptionId(option.id);
                          setDropdownOpen(false);
                        }}
                        style={{
                          ...dropdownRowBase,
                          backgroundColor: isSelected
                            ? "rgba(4, 120, 87, 0.08)"
                            : "transparent",
                          borderBottom: isLast ? "none" : dropdownRowBase.borderBottom,
                        }}
                      >
                        <span
                          style={{
                            width: 18,
                            display: "inline-flex",
                            justifyContent: "center",
                            color: brandDark,
                          }}
                        >
                          {isSelected ? <FaCheck size={12} /> : null}
                        </span>
                        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                          <span
                            style={{
                              fontSize: "0.85rem",
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? brandDark : "#0f172a",
                            }}
                          >
                            {option.label}
                          </span>
                          <span style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>
                            {formatDate(option.date)}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p style={{ marginTop: 4, fontSize: "0.8rem", color: "#64748b" }}>
              {formatDate(selectedOption?.date)}
            </p>
          )}
        </div>

        {/* Notes preview */}
        {election?.election_notes && (
          <div
            style={{
              fontSize: "0.8rem",
              color: "#334155",
              lineHeight: 1.6,
              backgroundColor: "#f8fafc",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 16,
              maxHeight: 140,
              overflowY: "auto",
              whiteSpace: "pre-line",
            }}
          >
            {election.election_notes}
          </div>
        )}

        {/* Options */}
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(3, 1fr)", marginTop: 16 }}>
          <button
            disabled={disabled}
            onClick={handleAddToGoogle}
            style={{ ...primaryBtnStyle, ...(disabled ? disabledStyle : {}) }}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.opacity = "0.92"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = disabled ? "0.5" : "1"; }}
          >
            Google
          </button>
          <button
            disabled={disabled}
            onClick={handleAddToOutlook}
            style={{ ...secondaryBtnStyle, ...(disabled ? disabledStyle : {}) }}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = "#f8fafc"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fff"; }}
          >
            Outlook
          </button>
          <button
            disabled={disabled}
            onClick={handleAddToIcs}
            style={{ ...secondaryBtnStyle, ...(disabled ? disabledStyle : {}) }}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = "#f8fafc"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fff"; }}
          >
            iCal (.ics)
          </button>
        </div>

        {calError && (
          <p style={{ marginTop: 12, fontSize: "0.875rem", color: "#dc2626", fontWeight: 500 }}>
            {calError}
          </p>
        )}

        <p style={{ marginTop: 16, fontSize: "0.75rem", color: "#64748b" }}>
          Your calendar app will open in a new tab to confirm the event.
        </p>
      </div>
    </div>
  );
}
